if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.liveRegion = this.querySelector('[id^="GalleryStatus"]');
        this.mql = window.matchMedia('(min-width: 750px)');
        this.swiperEnabled = this.dataset.swiperGallery === 'true';

        if (this.swiperEnabled) {
          this.initSwiperGallery();
        } else {
          this.initLegacyGallery();
        }
      }

      initLegacyGallery() {
        this.elements = {
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        if (!this.elements.viewer || !this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onLegacySlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeLegacyListSemantic();
      }

      initSwiperGallery() {
        this.elements = {
          viewer: this.querySelector('[data-gallery-main]'),
          thumbnails: this.querySelector('[data-gallery-thumbs]'),
          thumbnailsPrev: this.querySelector('[data-gallery-thumbs-prev]'),
          thumbnailsNext: this.querySelector('[data-gallery-thumbs-next]'),
          viewerPrev: this.querySelector('[data-gallery-main-prev]'),
          viewerNext: this.querySelector('[data-gallery-main-next]'),
        };

        if (!this.elements.viewer) return;
        if (this.viewerSwiper) {
          this.refreshSwipers();
          return;
        }

        const swiperConstructor = window.Swiper;
        if (!swiperConstructor) {
          this.deferSwiperInit();
          return;
        }

        this.setupSwipers(swiperConstructor);
        this.observeSwiperMutations();
      }

      deferSwiperInit() {
        if (this.swiperListenerAttached) return;
        this.swiperListenerAttached = true;

        const swiperScript = document.querySelector('script[src*="swiper-bundle"]');
        if (swiperScript) {
          swiperScript.addEventListener('load', () => this.initSwiperGallery(), { once: true });
        } else {
          window.addEventListener('load', () => this.initSwiperGallery(), { once: true });
        }
      }

      setupSwipers(SwiperLib) {
        const horizontalThumbsOnly = this.dataset.horizontalThumbs === 'true';
        const thumbsBreakpoints = horizontalThumbsOnly
          ? {}
          : {
              750: {
                direction: 'vertical',
              },
            };

        if (this.elements.thumbnails) {
          this.thumbsSwiper = new SwiperLib(this.elements.thumbnails, {
            direction: 'horizontal',
            slidesPerView: 'auto',
            spaceBetween: horizontalThumbsOnly ? 10 : 20,
            watchSlidesProgress: true,
            mousewheel: true,
            breakpoints: thumbsBreakpoints,
          });
        }

        this.viewerSwiper = new SwiperLib(this.elements.viewer, {
          speed: 500,
          slidesPerView: 1,
          thumbs: this.thumbsSwiper ? { swiper: this.thumbsSwiper } : undefined,
           navigation: {
              prevEl: this.elements.thumbnailsPrev,
              nextEl: this.elements.thumbnailsNext,
            },
        });

        this.viewerSwiper.on('slideChange', () => this.onSwiperSlideChange());
        this.bindThumbnailClicks();
        this.updateActiveState(this.viewerSwiper.activeIndex || 0);
      }

      observeSwiperMutations() {
        const target = this.elements.viewer?.querySelector('.swiper-wrapper');
        if (!target) return;

        this.swiperObserver = new MutationObserver(() => {
          this.refreshSwipers();
        });
        this.swiperObserver.observe(target, { childList: true });
      }

      refreshSwipers() {
        if (this.thumbsSwiper) {
          this.thumbsSwiper.updateSlides();
          this.thumbsSwiper.update();
        }
        this.viewerSwiper?.updateSlides();
        this.viewerSwiper?.update();
        this.bindThumbnailClicks();
        this.updateActiveState(this.viewerSwiper?.activeIndex || 0);
      }

      bindThumbnailClicks() {
        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((thumbnail, index) => {
          if (thumbnail.dataset.swiperBound === 'true') return;
          const button = thumbnail.querySelector('button');
          if (!button) return;
          button.addEventListener('click', () => this.viewerSwiper?.slideTo(index));
          thumbnail.dataset.swiperBound = 'true';
        });
      }

      onLegacySlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      onSwiperSlideChange() {
        const index = this.viewerSwiper.activeIndex || 0;
        this.updateActiveState(index);
        const activeMedia = this.elements.viewer.querySelectorAll('[data-media-id]')[index];
        if (activeMedia) {
          this.playActiveMedia(activeMedia);
          const mediaId = activeMedia.dataset.mediaId;
          const activeThumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
          if (activeThumbnail) {
            this.setActiveThumbnail(activeThumbnail);
            this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
          }
        }
      }

      updateActiveState(index) {
        const slides = this.elements.viewer?.querySelectorAll('[data-media-id]') || [];
        slides.forEach((slide, slideIndex) => {
          slide.classList.toggle('is-active', slideIndex === index);
        });

        if (!this.elements.thumbnails) return;
        const thumbnails = this.elements.thumbnails.querySelectorAll('[data-target]');
        thumbnails.forEach((thumb, thumbIndex) => {
          const button = thumb.querySelector('button');
          button?.setAttribute('aria-current', thumbIndex === index ? 'true' : 'false');
          thumb.classList.toggle('is-active', thumbIndex === index);
        });
      }

      setActiveMedia(mediaId, prepend) {
        if (this.swiperEnabled) {
          this.setActiveMediaSwiper(mediaId, prepend);
          return;
        }

        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail?.dataset.mediaPosition);
      }

      setActiveMediaSwiper(mediaId, prepend) {
        const slides = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
        const index = slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
        if (index === -1) return;

        if (prepend && index > 0) {
          const activeSlide = slides[index];
          activeSlide.parentElement.prepend(activeSlide);

          if (this.elements.thumbnails) {
            const thumb = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            thumb?.parentElement?.prepend(thumb);
          }

          this.refreshSwipers();
          this.viewerSwiper.slideTo(0, 0);
          if (this.thumbsSwiper) this.thumbsSwiper.slideTo(0, 0);
          this.updateActiveState(0);
          this.playActiveMedia(activeSlide);
          this.preventStickyHeader();
          return;
        }

        this.viewerSwiper.slideTo(index);
        this.updateActiveState(index);
        this.playActiveMedia(slides[index]);
        this.preventStickyHeader();
      }

      setActiveThumbnail(thumbnail) {
        if (!thumbnail) return;

        const button = thumbnail.querySelector('button');
        if (button) {
          thumbnail.parentElement
            .querySelectorAll('button')
            .forEach((element) => element.removeAttribute('aria-current'));
          button.setAttribute('aria-current', true);
        }

        if (!this.swiperEnabled) {
          if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
          return;
        }

        if (this.thumbsSwiper) {
          const index = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]')).indexOf(thumbnail);
          if (index > -1) this.thumbsSwiper.slideTo(index);
        }
      }

      announceLiveRegion(activeItem, position) {
        if (!this.liveRegion || !position) return;
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.liveRegion.setAttribute('aria-hidden', false);
          this.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia?.();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeLegacyListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
